/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useMemo, ComponentType, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, ListItemIcon, ListItemText, SvgIconProps, Typography } from '@mui/material';
import { LuiThreeDotMenu } from '@jda/lui-common-component-library-mui5';
import { AlertCircle, Edit, Globe, PlaceBuildingOffice, Trash } from '@jda/lui-common-icon-library-mui5';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { MRT_Row, MRT_RowData } from 'material-react-table';
import TableWithPaginator from '../../../shared/TableWithPaginator';
import PasswordPolicyDetails from './PasswordPolicyDetails/PasswordPolicyDetails';
import SetAsDefaultDialog from './SetAsDefaultDialog/SetAsDefaultDialog';
import DeleteDialog from './DeleteDialog/DeleteDialog';
import AssignToRealmDialog from './AssignToRealmDialog/AssignToRealmDialog';
import { ErrorMessage } from '../../../shared/ErrorMessage/ErrorMessage';
import {
  selectPasswordPolicies,
  selectPasswordPoliciesLoading,
  selectPasswordPoliciesError
} from '../../../store/passwordPolicies/passwordPoliciesSelector';
import { fetchPasswordPolicies } from '../../../store/passwordPolicies/passwordPolicies';
import { selectRealmSettings } from '../../../store/realmSettings/realmSettingsSelector';
import { selectResourcePermissions } from '../../../store/auth/authSelector';
import { checkResourcePermission } from '../../../shared/CheckPermissions/CheckPermission';
import { actions, resources } from '../../../shared/CheckPermissions/constants';
import { useStyles } from './styles';
import {
  POLICY_NAME,
  DEFAULT,
  ACTIONS,
  ASSIGN_TO_LOCATION,
  ASSIGN_TO_REALM,
  EDIT,
  DELETE,
  ROWS_PER_PAGE_LABEL,
  POLICY_TYPE,
  ASSOCIATED_IDPS,
  CUSTOM,
  BY_USER_STORE
} from './constants';
import { HARNESS_FEATURE_FLAG } from '../../../utility/constants';
import { PasswordPolicyModel } from '../../../models/PasswordPolicy/PasswordPolicy';
import { Location } from '../../../models/Locations/Locations';
import ManageLocationsDialog from '../../../shared/ManageLocationsDialog/ManageLocationsDialog';
import { MAX_PAGE_SIZE } from '../../../shared/ManageLocationsDialog/constants';
import { getPasswordPolicyLocations, assignPasswordPolicyToIdps } from '../../../services/setting';
import useWindowDimensions from '../../../utility/useWindowDimensions';
import { PASSWORD_POLICIES_SUB } from '../constants';

interface TabPanelProps {
  children: ReactNode;
  isActive: boolean;
  [propName: string]: any;
}

const TabPanel = ({ children, isActive, ...other }: TabPanelProps) => (
  <Box role="tabpanel" hidden={!isActive} {...other}>
    {isActive && <Box p={2}>{children}</Box>}
  </Box>
);

interface PasswordPolicyTableProps {
  nativeIDP: any;
  searchValue: string;
  handleClickOpenDialog: Function;
  extendedToolbar?: any;
}

const PasswordPolicyTable = ({ nativeIDP, searchValue, handleClickOpenDialog, extendedToolbar }: PasswordPolicyTableProps) => {
  const { height } = useWindowDimensions();
  const passwordPolicies = useSelector(selectPasswordPolicies);
  const passwordPoliciesLoading = useSelector(selectPasswordPoliciesLoading);
  const passwordPoliciesError = useSelector(selectPasswordPoliciesError);
  const realmSettings = useSelector(selectRealmSettings);
  const resourcePermissions = useSelector(selectResourcePermissions);
  const dispatch = useDispatch();
  const [locationAssigningState, setLocationAssigningState] = useState<{
    passwordPolicyId: string;
    locationsLoader: Promise<Location[]>;
  }>();
  const [defaultDialogPolicyId, setDefaultDialogPolicyId] = useState<string>();
  const [realmDialogPolicyId, setRealmDialogPolicyId] = useState<string>();
  const [deleteDialogPolicyId, setDeleteDialogPolicyId] = useState<string>();
  const isLocationEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.location);
  const { classes } = useStyles();
  const isRealmPolicyAvailable = passwordPolicies && passwordPolicies.some((policy: PasswordPolicyModel) => policy.idpsNames.length > 0);
  const filteredPasswordPolicies = useMemo(() => {
    if (!searchValue) {
      return passwordPolicies;
    }

    const searchValueUpperCased = searchValue.toUpperCase();

    return passwordPolicies.filter((passwordPolicy: PasswordPolicyModel) =>
      passwordPolicy.name.toUpperCase().includes(searchValueUpperCased)
    );
  }, [passwordPolicies, searchValue]);

  if (passwordPoliciesError) {
    return <ErrorMessage message={passwordPoliciesError} />;
  }

  const passwordPolicyEditPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.EDIT, resourcePermissions.resources[resources.PASSWORD_POLICY]);
  const passwordPolicyDeletePermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.DELETE, resourcePermissions.resources[resources.PASSWORD_POLICY]);
  const idpEditPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.EDIT, resourcePermissions.resources[resources.IDENTITY_PROVIDER]);

  const renderThreeDotMenuItem = (label: string, icon: ComponentType<SvgIconProps>) => {
    const Icon = icon;

    return (
      <>
        <ListItemIcon className={classes.actionIcon}>
          <Icon fontSize="small" />
        </ListItemIcon>
        <ListItemText data-testid={label}>{label}</ListItemText>
      </>
    );
  };

  const tableColumns = [
    {
      header: POLICY_NAME,
      accessorKey: 'name',
    },
    {
      header: POLICY_TYPE,
      accessorKey: 'isDefault',
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Typography variant="body1">
          {row.original.isDefault ? `${DEFAULT} ` : `${CUSTOM}`}
        </Typography>
      )
    },
    {
      header: ASSOCIATED_IDPS,
      accessorKey: 'idpsNames',
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Typography variant="body1">
          {row.original.idpsNames.length > 0 || (row.original.isDefault && !isRealmPolicyAvailable) ? BY_USER_STORE : '--'}
        </Typography>
      )
    },
    {
      header: '',
      accessorKey: 'menu',
      size: 60,
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => {
        const items = [
          ((row.original.idpsNames.length === 0 && !row.original.isDefault) ||
            (row.original.isDefault && row.original.idpsNames.length === 0 && isRealmPolicyAvailable)) &&
            idpEditPermission
            ? [
              {
                render: () => renderThreeDotMenuItem(ASSIGN_TO_REALM, Globe),
                onClick: () => {
                  setRealmDialogPolicyId(row.original.id);
                },
                autoClose: true
              }
            ]
            : [],
          isLocationEnabled && realmSettings?.enableLocationSegregation && idpEditPermission
            ? [
              {
                render: () => renderThreeDotMenuItem(ASSIGN_TO_LOCATION, PlaceBuildingOffice),
                onClick: () => {
                  setLocationAssigningState({
                    passwordPolicyId: row.original.id,
                    locationsLoader: getPasswordPolicyLocations(row.original.id, { pageSize: MAX_PAGE_SIZE }).then(
                      response => response.data
                    )
                  });
                },
                autoClose: true
              }
            ]
            : [],
          !row.original.isDefault && passwordPolicyEditPermission
            ? [
              {
                render: () => renderThreeDotMenuItem(EDIT, Edit),
                onClick: () => { handleClickOpenDialog('Edit', row.original) },
                autoClose: true
              }
            ]
            : [],
          !row.original.isDefault && passwordPolicyDeletePermission
            ? [
              {
                render: () => renderThreeDotMenuItem(DELETE, Trash),
                onClick: () => {
                  setDeleteDialogPolicyId(row.original.id);
                },
                autoClose: true
              }
            ]
            : []
        ].flat();

        if (items.length === 0) {
          return null;
        }

        return <LuiThreeDotMenu items={items} ariaLabel={ACTIONS} />;
      }
    }
  ];

  const detailPanel = (row: MRT_Row<MRT_RowData>) => {
    return (
      <>
        <TabPanel
          isActive={true}
          id={`policy-tabpanel-${row.original.id}`}
          aria-labelledby={`policy-tab-${row.original.id}`}
        >
          <PasswordPolicyDetails passwordPolicyId={row.original.id} />
        </TabPanel>
      </>
    );
  };

  return (
    <Box className={classes.tableContainer}>
      <TableWithPaginator
        loading={passwordPoliciesLoading}
        data={filteredPasswordPolicies}
        columns={tableColumns}
        withoutPagination={false}
        rowsPerPageOptions={[20, 50, 100]}
        maxTableBodyHeight={height - 290}
        defaultItemsPerPage={20}
        labelRowsPerPage={ROWS_PER_PAGE_LABEL}
        detailPanel={detailPanel}
        extendedToolbar={extendedToolbar}
        additionalContent={
          filteredPasswordPolicies ? (
            <Box className={classes.alertMsg}>
              <AlertCircle className={classes.warningIcon} />
              <Typography variant="body1">{PASSWORD_POLICIES_SUB}</Typography>
            </Box>
          ) : null
        }
      />
      {locationAssigningState && (
        <ManageLocationsDialog
          title={ASSIGN_TO_LOCATION}
          currentLocationsLoader={locationAssigningState.locationsLoader}
          maxToAdd={20}
          disableCurrentLocations={true}
          isLocationDisabled={location => !location.idpId}
          onSave={async toAdd => {
            await assignPasswordPolicyToIdps(
              locationAssigningState.passwordPolicyId,
              toAdd.flatMap(location => location.idpId ?? [])
            );
            fetchPasswordPolicies(dispatch);
          }}
          onClose={() => {
            setLocationAssigningState(undefined);
          }}
        />
      )}
      {defaultDialogPolicyId && (
        <SetAsDefaultDialog
          passwordPolicyId={defaultDialogPolicyId}
          onClose={() => {
            setDefaultDialogPolicyId(undefined);
          }}
        />
      )}
      {realmDialogPolicyId && (
        <AssignToRealmDialog
          nativeIDP={nativeIDP}
          passwordPolicyId={realmDialogPolicyId}
          onClose={() => {
            setRealmDialogPolicyId(undefined);
          }}
        />
      )}
      {deleteDialogPolicyId && (
        <DeleteDialog
          passwordPolicyId={deleteDialogPolicyId}
          onClose={() => {
            setDeleteDialogPolicyId(undefined);
          }}
        />
      )}
    </Box>
  );
};

export default PasswordPolicyTable;
